import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Activity } from 'lucide-react';
import { cn } from '@/lib/analytics/utils';
import { useTranslation } from 'react-i18next';
import logo from '../../dark_logo.webp';

const Navbar = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [user, setUser] = useState(null);
    const location = useLocation();

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch {
                setUser(null);
            }
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Home', path: '/' },
        {
            name: 'Features',
            path: '/features',
            dropdown: [
                { name: 'MedCoreX Cortex', path: '/product/ai' },
                { name: 'Dynamic Antibiogram', path: '/product/antibiogram' },
                { name: 'Renal Dosing Guard', path: '/product/renal' },
                { name: 'Clinical Calculators', path: '/calculator' },
                { name: 'All Features', path: '/features' },
            ]
        },
        { name: 'Clinical Tools', path: '/ai-hub', isSpecial: true },
        { name: 'Pearls', path: '/clinical-pearls' },
        { name: 'Community', path: '/community' },
        { name: 'Pricing', path: '/pricing' },
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => {
        if (path === '/' && location.pathname !== '/') return false;
        return location.pathname.startsWith(path);
    };

    return (
        <>
            <nav
                className={`fixed top-0 w-full z-50 transition-all duration-300 ${location.pathname.startsWith('/community')
                    ? 'bg-slate-100/95 dark:bg-[#0B1120] border-b border-slate-200 dark:border-white/5 shadow-md shadow-emerald-900/5 dark:shadow-emerald-900/10'
                    : (scrolled || location.pathname !== '/'
                        ? 'bg-white/90 dark:bg-slate-950/90 backdrop-blur-md shadow-lg shadow-slate-200/50 dark:shadow-emerald-900/10 border-b border-slate-200 dark:border-emerald-900/20'
                        : 'bg-transparent border-b border-slate-200/40 dark:border-white/5')
                    }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 lg:h-20">
                        <Link to="/" className="flex items-center gap-2 group" aria-label="MedCoreX home">
                            <img src={logo} alt="MedCoreX" width="220" height="80" className="h-20 lg:h-24 w-auto object-contain" fetchPriority="high" />
                        </Link>

                        <div className="hidden md:block">
                            <div className="ml-10 flex items-baseline space-x-8">
                                {navLinks.map((link) => (
                                    link.dropdown ? (
                                        <div key={link.name} className="relative group">
                                            <Link
                                                to={link.path}
                                                className={`flex items-center gap-1 text-sm font-medium transition-all duration-200 hover:text-emerald-600 dark:hover:text-emerald-300 ${isActive(link.path) ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-200'}`}
                                            >
                                                {link.name} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
                                            </Link>

                                            <div className="absolute top-full left-0 pt-4 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                                                <div className="bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden p-2">
                                                    {link.dropdown.map((subItem) => (
                                                        <Link
                                                            key={subItem.name}
                                                            to={subItem.path}
                                                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                                        >
                                                            {subItem.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Link
                                            key={link.name}
                                            to={link.path}
                                            className={cn(
                                                'text-sm font-medium transition-all duration-200',
                                                link.isSpecial
                                                    ? 'px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.08)] dark:shadow-[0_0_15px_rgba(16,185,129,0.1)] mt-[-2px] motion-safe:animate-pulse hover:animate-none'
                                                    : (isActive(link.path) ? 'text-emerald-600 dark:text-emerald-300 font-bold' : 'text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-300')
                                            )}
                                        >
                                            {link.isSpecial && <Activity size={14} className="inline mr-1.5 mb-0.5" />}
                                            {link.name}
                                        </Link>
                                    )
                                ))}
                            </div>
                        </div>

                        <div className="hidden md:flex items-center gap-4">
                            {user ? (
                                <div className="relative group">
                                    <Link
                                        to="/dashboard"
                                        aria-label="Open dashboard"
                                        className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 transition-all cursor-pointer"
                                    >
                                        {user.profile_image ? (
                                            <img src={user.profile_image} alt="Profile" width="32" height="32" className="w-8 h-8 rounded-full object-cover border border-emerald-500/50" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 text-sm font-bold shadow-inner">
                                                {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                        )}
                                    </Link>

                                    <div className="absolute top-full right-0 pt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                                        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden py-2">
                                            <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800 mb-1">
                                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user.name}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email || user.username}</p>
                                            </div>
                                            <Link to="/dashboard" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">Dashboard</Link>
                                            <Link to="/profile" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">Profile Settings</Link>
                                            <div className="h-px bg-slate-200 dark:bg-slate-800 my-1"></div>
                                            <button
                                                onClick={() => {
                                                    localStorage.removeItem('user');
                                                    localStorage.removeItem('token');
                                                    window.location.href = '/login';
                                                }}
                                                className="w-full text-left block px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                            >
                                                Log Out
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <Link
                                    to="/login"
                                    className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 text-sm"
                                >
                                    {t('navbar.login', 'Login')}
                                </Link>
                            )}
                        </div>

                        <div className="-mr-2 flex md:hidden">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                aria-label="Toggle mobile menu"
                                className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors focus:outline-none"
                            >
                                {isOpen ? <X className="block w-6 h-6" /> : <Menu className="block w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            <div className={`fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-xl transition-all duration-300 md:hidden flex flex-col justify-center items-center ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute top-6 right-6">
                    <button
                        onClick={() => setIsOpen(false)}
                        aria-label="Close mobile menu"
                        className="p-2 rounded-full bg-slate-800 text-slate-200 hover:text-white transition-colors"
                    >
                        <X className="w-8 h-8" />
                    </button>
                </div>

                <div className="space-y-8 text-center">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={cn(
                                'block text-2xl font-medium transition-all duration-300 transform',
                                link.isSpecial
                                    ? 'px-8 py-3 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 scale-105'
                                    : (isActive(link.path) ? 'text-emerald-600 dark:text-emerald-300 scale-110' : 'text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:scale-105')
                            )}
                        >
                            {link.isSpecial && <Activity size={20} className="inline mr-2 mb-1" />}
                            {link.name}
                        </Link>
                    ))}
                    <div className="pt-8 flex flex-col items-center gap-4">
                        {user ? (
                            <div className="flex flex-col gap-3 w-full max-w-xs">
                                <Link
                                    to="/dashboard"
                                    onClick={() => setIsOpen(false)}
                                    className="flex items-center justify-center gap-3 px-8 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold shadow-lg transition-all"
                                >
                                    {user.profile_image ? (
                                        <img src={user.profile_image} alt="" width="24" height="24" className="w-6 h-6 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-slate-950 text-xs font-bold">
                                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                        </div>
                                    )}
                                    Dashboard
                                </Link>
                                <Link
                                    to="/profile"
                                    onClick={() => setIsOpen(false)}
                                    className="block px-8 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-medium transition-all"
                                >
                                    Profile Settings
                                </Link>
                                <button
                                    onClick={() => {
                                        setIsOpen(false);
                                        localStorage.removeItem('user');
                                        localStorage.removeItem('token');
                                        window.location.href = '/login';
                                    }}
                                    className="block px-8 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium transition-all text-center"
                                >
                                    Log Out
                                </button>
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                onClick={() => setIsOpen(false)}
                                className="inline-block px-12 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-lg font-bold shadow-2xl shadow-emerald-500/20 transition-all hover:scale-105 hover:shadow-emerald-500/40"
                            >
                                {t('navbar.login', 'Login')}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
